import { useState } from "react";
import { Coins, Gift, Ticket, Award } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RedemptionView } from "@/components/RedemptionView";
import { useAuth } from "@/context/AuthContext";
import { API, api, errText } from "@/lib/api";
import { formatDay } from "@/lib/constants";
import { Linkify } from "@/components/Linkify";

export const REWARD_TYPES = {
  promo: { name: "Промокод", icon: Ticket },
  certificate: { name: "Сертификат", icon: Award },
  gift: { name: "Подарок", icon: Gift },
};

function Cover({ r }) {
  const T = REWARD_TYPES[r.reward_type] || REWARD_TYPES.gift;
  if (r.has_cover) return <img src={`${API}/rewards/${r.id}/cover`} alt={r.title} className="w-full h-full object-cover" />;
  return <div className="w-full h-full grid place-items-center bg-[#1f5f42] grain"><T.icon className="h-12 w-12 text-[#a3e6be]" /></div>;
}

export function RewardCard({ r, onRedeemed }) {
  const { user, refreshMe } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const T = REWARD_TYPES[r.reward_type] || REWARD_TYPES.gift;
  const balance = user.role === "admin" ? Infinity : user.coins;
  const pct = Math.min(100, Math.round((balance / r.cost) * 100));
  const soldOut = r.reward_type === "promo" && r.stock === 0;
  const expired = !!r.valid_until && r.valid_until < new Date().toISOString().slice(0, 10);
  const canBuy = balance >= r.cost && !soldOut && !expired;

  const redeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rewards/${r.id}/redeem`);
      setConfirm(false); setResult(data.redemption); refreshMe(); onRedeemed?.();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-[#d1e7dd] overflow-hidden flex flex-col hover:shadow-md transition-shadow" data-testid={`reward-card-${r.id}`}>
      <div className="relative aspect-[16/9]">
        <Cover r={r} />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-[#1f5f42] text-xs font-bold"><T.icon className="h-3.5 w-3.5" />{T.name}</span>
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-500/30 font-bold text-sm" data-testid="reward-cost"><Coins className="h-3.5 w-3.5" />{r.cost}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="font-display font-bold text-lg text-[#0b2e20]">{r.title}</div>
        {r.description && <p className="text-sm text-[#4b6b5c] mt-1 whitespace-pre-wrap break-words"><Linkify text={r.description} /></p>}
        {r.delivery_method && <p className="text-xs text-[#2e7d56] mt-2 font-semibold break-words" data-testid="reward-delivery-method">Способ получения: <Linkify text={r.delivery_method} /></p>}
        {r.reward_type === "promo" && <p className="text-xs text-[#6b8a7b] mt-1" data-testid="reward-stock">Осталось: {r.stock}</p>}
        <p className="text-xs text-[#6b8a7b] mt-1" data-testid="reward-valid-until">{r.valid_until ? `Действует до ${formatDay(r.valid_until)}` : "Бессрочно"}</p>
        <div className="flex-1" />
        <div className="mt-4">
          <div className="h-2 rounded-full bg-[#e8f5ed] overflow-hidden"><div className="h-full bg-[#2e7d56] rounded-full" style={{ width: `${pct}%` }} /></div>
          <div className="text-xs text-[#6b8a7b] mt-1.5">{pct >= 100 ? "Коинов хватает" : `Накоплено ${pct}%`}</div>
        </div>
        <Button disabled={!canBuy} onClick={() => setConfirm(true)} data-testid={`reward-exchange-${r.id}`}
          className="mt-4 rounded-full bg-[#1f5f42] hover:bg-[#174731] disabled:bg-[#e8f5ed] disabled:text-[#6b8a7b] disabled:opacity-100">
          {expired ? "Срок истёк" : soldOut ? "Закончились" : canBuy ? "Обменять" : "Недостаточно коинов"}
        </Button>
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="redeem-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Обменять {r.cost} коинов?</DialogTitle>
            <DialogDescription>Вы получите «{r.title}» сразу после обмена.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirm(false)} data-testid="redeem-cancel-button">Отмена</Button>
            <Button disabled={busy} className="rounded-full bg-[#1f5f42] hover:bg-[#174731]" onClick={redeem} data-testid="redeem-confirm-button">{busy ? "Обмен..." : "Подтвердить"}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent data-testid="redeem-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Готово! Ваша награда</DialogTitle>
            <DialogDescription>{r.title}. Награда сохранится в разделе «Мои награды»{r.valid_until ? `, действует до ${formatDay(r.valid_until)}` : "."}</DialogDescription>
          </DialogHeader>
          {result && <RedemptionView d={result} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
